import React, { useState, useMemo } from "react"
import { library } from "@fortawesome/fontawesome-svg-core"
import { fas } from "@fortawesome/free-solid-svg-icons"
library.add(fas)

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Card from "@react/shared/ui/Card"

const round = (n, d = 2) => {
  if (n == null || isNaN(n)) return 0
  const m = Math.pow(10, d)
  return Math.round(n * m) / m
}

const num = v => {
  if (v === "" || v == null) return 0
  const n = parseFloat(v)
  return isNaN(n) ? 0 : n
}

// A breakdown row only counts when it has a quantity: 0 or blank means the
// cost category is not part of the article.
const rowFilled = b => num(b.quantity) > 0

// A saved price of 0 is a real price: only the rows not saved yet show it blank.
const priceValue = (b, price) => (price === 0 && !b.id ? "" : price)

const App = ({ data }) => {
  if (!data) return null

  const isEdit         = Boolean(data.mbc_line.id)
  const currencySymbol = data.currency_symbol || "€"

  const [code,        setCode]        = useState(data.mbc_line.code || "")
  const [label,       setLabel]       = useState(data.mbc_line.label || "")
  const [unit,        setUnit]        = useState(data.mbc_line.unit || "")
  const [description, setDescription] = useState(data.mbc_line.description || "")
  const [breakdowns,  setBreakdowns]  = useState(
    (data.mbc_line.breakdowns || []).map(b => {
      const purchase = num(b.purchase_price)
      const sale     = num(b.unit_price)
      const coef     = purchase > 0 && sale > 0 ? round(sale / purchase, 3) : ""
      return { ...b, coefficient: coef }
    })
  )
  const [errors,      setErrors]      = useState(null)
  const [submitting,  setSubmitting]  = useState(false)

  const totals = useMemo(() => {
    const purchase = breakdowns.reduce((sum, b) => sum + num(b.quantity) * num(b.purchase_price), 0)
    const sale     = breakdowns.reduce((sum, b) => sum + num(b.quantity) * num(b.unit_price), 0)
    const coef     = purchase > 0 ? sale / purchase : null
    return { purchase: round(purchase, 2), sale: round(sale, 2), coef: coef != null ? round(coef, 3) : null }
  }, [breakdowns])

  // Three-way binding between purchase_price, coefficient, unit_price.
  // Changing one recomputes the dependent field so the line always satisfies
  //   unit_price = purchase_price × coefficient
  const updateBreakdown = (index, field, value) => {
    setBreakdowns(prev => {
      const next       = [...prev]
      const current    = { ...next[index] }
      const purchase   = num(current.purchase_price)
      const sale       = num(current.unit_price)
      const stringVal  = value

      current[field] = stringVal

      if (field === "purchase_price") {
        const newPurchase = num(stringVal)
        if (newPurchase > 0 && sale > 0) {
          current.coefficient = round(sale / newPurchase, 3)
        } else {
          current.coefficient = ""
        }
      } else if (field === "unit_price") {
        const newSale = num(stringVal)
        if (purchase > 0 && newSale > 0) {
          current.coefficient = round(newSale / purchase, 3)
        } else {
          current.coefficient = ""
        }
      } else if (field === "coefficient") {
        const newCoef = num(stringVal)
        if (purchase > 0 && newCoef > 0) {
          current.unit_price = round(purchase * newCoef, 2)
        }
      }

      next[index] = current
      return next
    })
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (!breakdowns.some(rowFilled)) {
      setErrors({ base: [I18n.t("mbc_contracts.mbc_lines.breakdowns.at_least_one")] })
      return
    }

    setSubmitting(true)
    setErrors(null)

    const payload = {
      mbc_line: {
        code,
        label,
        unit,
        description,
        purchase_price: totals.purchase,
        unit_price:     totals.sale,
        mbc_line_breakdowns_attributes: breakdowns.reduce((attributes, b, idx) => {
          if (!rowFilled(b)) {
            if (b.id) attributes.push({ id: b.id, _destroy: true })
            return attributes
          }

          attributes.push({
            id:             b.id,
            cost_category:  b.cost_category,
            unit:           b.unit,
            quantity:       num(b.quantity),
            unit_price:     num(b.unit_price),
            purchase_price: num(b.purchase_price),
            sort_order:     idx
          })
          return attributes
        }, [])
      }
    }

    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content
      const res  = await fetch(data.paths.submit, {
        method:  data.method,
        headers: {
          "Content-Type":     "application/json",
          "X-CSRF-Token":     csrf,
          "Accept":           "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin",
        body:        JSON.stringify(payload)
      })
      const body = await res.json().catch(() => null)
      if (res.ok) {
        window.location.href = data.paths.success_redirect
      } else {
        setErrors(body)
        setSubmitting(false)
      }
    } catch (e) {
      setErrors({ base: ["Erreur réseau"] })
      setSubmitting(false)
    }
  }

  const errorMessages = errors ? Object.entries(errors).flatMap(([key, msgs]) => {
    const list = Array.isArray(msgs) ? msgs : [msgs]
    return key === "base" ? list : list.map(m => `${key} ${m}`)
  }) : []

  return (
    <div className="font-sans bg-slate-50 min-h-screen text-slate-900">
      <div className="px-8 py-8 max-w-5xl mx-auto">
        <div className="mb-6 flex items-center gap-3">
          <a
            href      = {data.paths.cancel}
            className = "inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-700"
            title     = {I18n.t("button.back", { defaultValue: "Retour" })}
          >
            <FontAwesomeIcon icon="arrow-left" className="text-[13px]" />
          </a>
          <h1 className="text-2xl font-semibold text-slate-900">
            {isEdit
              ? I18n.t("mbc_contracts.mbc_lines.edit", { defaultValue: "Modifier la ligne" })
              : I18n.t("mbc_contracts.mbc_lines.add",  { defaultValue: "Ajouter une ligne" })}
          </h1>
        </div>

        {errorMessages.length > 0 && (
          <Card padding="md" className="rounded-lg bg-rose-50 border-rose-200 mb-4">
            <div className="flex items-start gap-3 text-sm text-rose-800">
              <FontAwesomeIcon icon="circle-exclamation" className="text-rose-600 mt-0.5" />
              <ul className="space-y-0.5">
                {errorMessages.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            </div>
          </Card>
        )}

        <form onSubmit={handleSubmit}>
          <Card padding="md" className="rounded-lg space-y-4">
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-3">
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  {I18n.t("mbc_contracts.mbc_lines.ref")} *
                </label>
                <input
                  type      = "text"
                  value     = {code}
                  onChange  = {e => setCode(e.target.value)}
                  required
                  className = "w-full h-10 px-3 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:border-brand-500 focus:ring-[3px] focus:ring-brand-500/20"
                />
              </div>
              <div className="col-span-7">
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  {I18n.t("mbc_contracts.mbc_lines.label")} *
                </label>
                <input
                  type      = "text"
                  value     = {label}
                  onChange  = {e => setLabel(e.target.value)}
                  required
                  className = "w-full h-10 px-3 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:border-brand-500 focus:ring-[3px] focus:ring-brand-500/20"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  {I18n.t("mbc_contracts.mbc_lines.unit")} *
                </label>
                <input
                  type      = "text"
                  value     = {unit}
                  onChange  = {e => setUnit(e.target.value)}
                  required
                  className = "w-full h-10 px-3 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:border-brand-500 focus:ring-[3px] focus:ring-brand-500/20"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                {I18n.t("mbc_contracts.mbc_lines.description")}
              </label>
              <textarea
                value     = {description}
                onChange  = {e => setDescription(e.target.value)}
                rows      = {2}
                className = "w-full px-3 py-2 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:border-brand-500 focus:ring-[3px] focus:ring-brand-500/20"
              />
            </div>
          </Card>

          <Card className="rounded-lg mt-4">
            <div className="px-4 py-3 border-b border-slate-200">
              <h2 className="text-base font-semibold text-slate-900">
                {I18n.t("mbc_contracts.mbc_lines.breakdowns.title")}
              </h2>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50/50 text-[11px] uppercase tracking-wider text-slate-500 font-medium">
                  <tr>
                    <th className="text-left  px-4 py-2.5" style={{ width: "18%" }}>{I18n.t("mbc_contracts.mbc_lines.breakdowns.cost_category")}</th>
                    <th className="text-right px-3 py-2.5" style={{ width: "13%" }}>{I18n.t("mbc_contracts.mbc_lines.breakdowns.quantity")}</th>
                    <th className="text-right px-3 py-2.5" style={{ width: "13%" }}>{I18n.t("mbc_contracts.mbc_lines.breakdowns.unit")}</th>
                    <th className="text-right px-3 py-2.5" style={{ width: "16%" }}>{I18n.t("mbc_contracts.mbc_lines.breakdowns.purchase_price")} ({currencySymbol})</th>
                    <th className="text-right px-3 py-2.5" style={{ width: "13%" }}>{I18n.t("mbc_contracts.mbc_lines.breakdowns.coefficient")}</th>
                    <th className="text-right px-3 py-2.5" style={{ width: "16%" }}>{I18n.t("mbc_contracts.mbc_lines.breakdowns.unit_price")} ({currencySymbol})</th>
                  </tr>
                </thead>
                <tbody>
                  {breakdowns.map((b, idx) => {
                    return (
                      <tr key={idx} className="border-t border-slate-100">
                        <td className="px-4 py-2">
                          <div className="h-10 px-3 flex items-center bg-slate-50 border border-slate-200 rounded-md text-slate-700">
                            {b.cost_category_label}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type     = "number"
                            step     = "0.01"
                            min      = "0"
                            value    = {b.quantity === 0 ? "" : b.quantity}
                            onChange = {e => updateBreakdown(idx, "quantity", e.target.value)}
                            className= "w-full h-10 px-3 bg-white border border-slate-200 rounded-md text-sm text-right focus:outline-none focus:border-brand-500 focus:ring-[3px] focus:ring-brand-500/20"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type     = "text"
                            value    = {b.unit || ""}
                            onChange = {e => updateBreakdown(idx, "unit", e.target.value)}
                            className= "w-full h-10 px-3 bg-white border border-slate-200 rounded-md text-sm text-right focus:outline-none focus:border-brand-500 focus:ring-[3px] focus:ring-brand-500/20"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type     = "number"
                            step     = "0.01"
                            min      = "0"
                            required = {rowFilled(b)}
                            value    = {priceValue(b, b.purchase_price)}
                            onChange = {e => updateBreakdown(idx, "purchase_price", e.target.value)}
                            className= "w-full h-10 px-3 bg-white border border-slate-200 rounded-md text-sm text-right focus:outline-none focus:border-brand-500 focus:ring-[3px] focus:ring-brand-500/20"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type     = "number"
                            step     = "0.001"
                            min      = "0"
                            value    = {b.coefficient ?? ""}
                            onChange = {e => updateBreakdown(idx, "coefficient", e.target.value)}
                            className= "w-full h-10 px-3 bg-white border border-slate-200 rounded-md text-sm text-right focus:outline-none focus:border-brand-500 focus:ring-[3px] focus:ring-brand-500/20"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type     = "number"
                            step     = "0.01"
                            min      = "0"
                            required = {rowFilled(b)}
                            value    = {priceValue(b, b.unit_price)}
                            onChange = {e => updateBreakdown(idx, "unit_price", e.target.value)}
                            className= "w-full h-10 px-3 bg-white border border-slate-200 rounded-md text-sm text-right focus:outline-none focus:border-brand-500 focus:ring-[3px] focus:ring-brand-500/20"
                          />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot className="bg-slate-50/50">
                  <tr className="border-t border-slate-200">
                    <td colSpan={3} className="px-4 py-3 text-right text-sm font-semibold text-slate-900">
                      {I18n.t("quotes.qline_service_blueprints.totals", { defaultValue: "Totaux" })}
                    </td>
                    <td className="px-3 py-3 text-right text-sm font-semibold text-slate-900 tabular-nums">
                      {totals.purchase} {currencySymbol}
                    </td>
                    <td className="px-3 py-3 text-right text-sm font-semibold text-slate-900 tabular-nums">
                      {totals.coef != null ? `×${totals.coef}` : "—"}
                    </td>
                    <td className="px-3 py-3 text-right text-sm font-semibold text-slate-900 tabular-nums">
                      {totals.sale} {currencySymbol}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>

          <div className="flex items-center justify-end gap-2 mt-6">
            <a
              href      = {data.paths.cancel}
              className = "inline-flex items-center gap-2 h-9 px-4 rounded-md bg-white border border-slate-200 text-[13px] font-medium text-slate-700 hover:bg-slate-50"
            >
              {I18n.t("button.cancel", { defaultValue: "Annuler" })}
            </a>
            <button
              type      = "submit"
              disabled  = {submitting}
              className = "inline-flex items-center gap-2 h-9 px-4 rounded-md bg-brand-500 text-[13px] font-medium text-white hover:bg-brand-600 disabled:opacity-50"
            >
              {submitting && <FontAwesomeIcon icon="circle-notch" className="text-[12px] animate-spin" />}
              {I18n.t("button.save", { defaultValue: "Enregistrer" })}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default App
